/**
 * Content → blog post editor. Autosave every 10 s goes to the post's draft; explicit saves make revisions. A live post keeps
 * its public version until "Update" (docs: api/docs/SITES.md "Blog editor").
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Col, Collapse, DatePicker, Dropdown, Form, Input, InputNumber, Popconfirm, Result, Row, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, DownOutlined, ExportOutlined, HistoryOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import TagsInput from '../../../shared/components/TagsInput';
import { QUIET } from './api';
import { reportAiError, runAi } from './ai';
import BlockEditor from './blog/BlockEditor';
import CoverPicker from './blog/CoverPicker';
import RevisionsDrawer from './blog/RevisionsDrawer';
import SeoPanel from './blog/SeoPanel';
import { plainText } from './blog/articleSchema';
import ShopAiButton from './ShopAiButton';
import { StatusTag, fmtDate, listOf, siteUrl, sitePath, toastErr, useLeaveGuard, useSite } from './common';

const { Text } = Typography;
const EMPTY = { title: '', slug: '', body_html: '', excerpt: '', cover: null, author: '', tags: [], status: 'draft', published_at: null, seo: { title: '', description: '', image: '', canonical: '', noindex: false } };
const AUTOSAVE_MS = 10000;

const fieldsOf = (p) => ({
    title: p.title, slug: p.slug, body_html: p.body_html, excerpt: p.excerpt, author: p.author, tags: p.tags,
    cover: p.cover?.src ? p.cover : null, seo: p.seo, published_at: p.published_at || null,
    featured: !!p.featured, pinned: !!p.pinned, author_id: p.author_id || null, category_ids: p.category_ids || [], series_id: p.series_id || null,
    series_position: p.series_position || null,
});
/** server post → editor state: the draft, when there is one, is what the author was working on */
const merge = (d) => {
    const m = { ...EMPTY, category_ids: (d?.categories || []).map((c) => c.id), ...d, ...(d?.draft || {}) };
    return { ...m, public_slug: d?.slug, seo: { ...EMPTY.seo, ...(d?.seo || {}), ...(d?.draft?.seo || {}) } };
};

/** The post now has an id: the address bar shows it (reload, sharing) without a route change, which would reset the editor. */
const showUrl = (siteId, id) => window.history.replaceState(window.history.state, '', sitePath(siteId, `blog/${id}`));

const PostEdit = () => {
    const { t } = useTranslation();
    // /blog/new and /blog/:id are one route (:postId = "new")
    const { postId: routeId } = useParams();
    // after autosave created the post the router still says "new": the address bar has the id (showUrl)
    const postId = routeId === 'new' ? (window.location.pathname.match(/\/blog\/(\d+)\/?$/)?.[1] || null) : routeId;
    const isNew = !postId;
    const navigate = useNavigate();
    const { api, site, siteId } = useSite();
    const [p, setP] = useState(isNew ? EMPTY : null);
    const [err, setErr] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [slugTouched, setSlugTouched] = useState(!isNew);
    const [history, setHistory] = useState(false);
    const [aiBusy, setAiBusy] = useState(null);
    const [docKey, setDocKey] = useState(0);
    const [lookup, setLookup] = useState({ authors: [], categories: [], series: [], defaultAuthor: '' });
    useLeaveGuard(dirty);
    const pRef = useRef(p);
    pRef.current = p;
    const dirtyRef = useRef(false);
    const idRef = useRef(postId || null);
    const inFlight = useRef(false);

    const adopt = useCallback((d) => { setP(merge(d)); setSavedAt(d?.draft_saved_at || null); }, []);

    useEffect(() => {
        idRef.current = postId || null;
        if (isNew) { setP(EMPTY); setSlugTouched(false); setDocKey((k) => k + 1); return undefined; }
        let live = true;
        api.get(`/posts/${postId}/editor`).then((d) => { if (live) { adopt(d); setSlugTouched(true); setDocKey((k) => k + 1); } }).catch((e) => live && setErr(e));
        return () => { live = false; };
    }, [api, postId, isNew, adopt]);

    useEffect(() => {
        Promise.all([api.get('/authors', null, QUIET), api.get('/post-terms', { kind: 'category' }, QUIET), api.get('/post-terms', { kind: 'series' }, QUIET)])
            .then(([a, c, s]) => setLookup({ authors: listOf(a), categories: listOf(c), series: listOf(s), defaultAuthor: a?.default_name || '' })).catch(() => {});
    }, [api]);

    const set = useCallback((patch, touchSlug) => {
        setP((x) => ({ ...x, ...(typeof patch === 'function' ? patch(x) : patch) }));
        if (touchSlug) setSlugTouched(true);
        dirtyRef.current = true;
        setDirty(true);
    }, []);

    // the URL of a new post follows the title until the author edits it (the server knows the site language)
    useEffect(() => {
        if (slugTouched || !p?.title?.trim()) return undefined;
        const h = setTimeout(() => {
            api.get('/posts/slug', { title: p.title, ...(idRef.current ? { except: idRef.current } : {}) }, QUIET)
                .then((d) => d?.slug && setP((x) => ({ ...x, slug: d.slug }))).catch(() => {});
        }, 500);
        return () => clearTimeout(h);
    }, [api, p?.title, slugTouched]);

    const autosave = useCallback(async () => {
        const cur = pRef.current;
        if (!dirtyRef.current || inFlight.current || !cur?.title?.trim()) return;
        inFlight.current = true;
        dirtyRef.current = false;
        try {
            if (!idRef.current) {
                const d = await api.post('/posts/editor', { ...fieldsOf(cur), action: 'save' }, QUIET);
                idRef.current = String(d.id);
                setP((x) => ({ ...x, id: d.id, slug: d.slug, status: d.status }));
                setSlugTouched(true);
                setSavedAt(d.updated_at);
                showUrl(siteId, d.id);
            } else {
                const d = await api.put(`/posts/${idRef.current}/autosave`, fieldsOf(cur), QUIET);
                setSavedAt(d.draft_saved_at);
                setP((x) => ({ ...x, draft: true }));
            }
            if (!dirtyRef.current) setDirty(false);
        } catch (_) {
            dirtyRef.current = true; // tried again in 10 s
        } finally { inFlight.current = false; }
    }, [api, siteId]);
    useEffect(() => { const h = setInterval(autosave, AUTOSAVE_MS); return () => clearInterval(h); }, [autosave]);

    const commit = async (action) => {
        if (!p.title.trim()) { message.error(t('Title is required')); return; }
        setSaving(true);
        dirtyRef.current = false;
        try {
            const body = { ...fieldsOf(p), action };
            const d = idRef.current ? await api.put(`/posts/${idRef.current}/editor`, body) : await api.post('/posts/editor', body);
            message.success(t(action === 'publish' ? 'Published' : action === 'update' ? 'Updated' : action === 'unpublish' ? 'Unpublished' : 'Saved'));
            setDirty(false);
            if (!idRef.current) { idRef.current = String(d.id); showUrl(siteId, d.id); }
            adopt(d);
            setSlugTouched(true);
        } catch (e) { dirtyRef.current = true; toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };
    const discard = async () => {
        try { const d = await api.del(`/posts/${idRef.current}/draft`); adopt(d); setDocKey((k) => k + 1); dirtyRef.current = false; setDirty(false); } catch (e) { toastErr(e); }
    };
    const remove = async () => {
        try { await api.del(`/posts/${idRef.current}`); message.success(t('Deleted')); dirtyRef.current = false; setDirty(false); navigate(sitePath(siteId, 'blog')); } catch (e) { toastErr(e); }
    };

    const assist = async (action, apply) => {
        const text = plainText(p.body_html);
        if (text.length < 3) { message.info(t('Write some text first')); return; }
        setAiBusy(action);
        try { apply(await runAi(siteId, 'post_assist', { action, text: text.slice(0, 16000), title: p.title })); } catch (e) { reportAiError(e); } finally { setAiBusy(null); }
    };

    if (err) return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Post not found') : (err.error || t('Failed to load'))} extra={<Button onClick={() => navigate(sitePath(siteId, 'blog'))}>{t('Blog posts')}</Button>} />;
    if (!p) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;

    const base = siteUrl(site);
    const url = base && p.slug ? `${base}/blog/${encodeURIComponent(p.slug)}` : '';
    const future = p.published_at && dayjs(String(p.published_at).replace(' ', 'T')).isAfter(dayjs());
    const scheduled = p.status === 'published' && (p.status_label === 'scheduled' || future);
    const published = p.status === 'published';
    const live = !!p.live;
    const hasId = !!idRef.current;
    const hasDraft = hasId && published && !!(p.draft || dirty);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <Button type="text" icon={<ArrowLeftOutlined />} aria-label={t('Blog posts')} onClick={() => navigate(sitePath(siteId, 'blog'))} />
                <Text strong style={{ fontSize: 18 }} ellipsis>{p.title || t('Write post')}</Text>
                {hasId && (scheduled
                    ? <Tag color="blue" data-testid="scheduled-tag">{t('Scheduled for {{date}}', { date: fmtDate(p.published_at) })}</Tag>
                    : <StatusTag value={p.status} />)}
                {hasDraft && <Tag color="gold">{t('Unpublished changes')}</Tag>}
                {live && url && <a href={url} target="_blank" rel="noopener noreferrer"><ExportOutlined /> {t('View on site')}</a>}
                <div style={{ flex: 1 }} />
                {hasId && <Button icon={<HistoryOutlined />} onClick={() => setHistory(true)}>{t('Revisions')}</Button>}
                {!hasId && <ShopAiButton kind="post" onResult={(r) => { if (r?.id) navigate(sitePath(siteId, `blog/${r.id}`), { replace: true }); }} />}
            </div>
            {hasId && p.draft && published && (
                <Alert style={{ marginBottom: 12 }} type="info" showIcon message={t('You are editing a draft. The site keeps the published version until you update.')}
                    action={<Popconfirm title={t('Discard the draft?')} okButtonProps={{ danger: true }} onConfirm={discard}><Button size="small">{t('Discard')}</Button></Popconfirm>} />
            )}
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Card style={{ marginBottom: 16 }}>
                        <Form layout="vertical">
                            <Form.Item label={t('Title')} required>
                                <Input value={p.title} size="large" onChange={(e) => set({ title: e.target.value })} />
                            </Form.Item>
                            <Form.Item label={t('Content')}>
                                <BlockEditor siteId={siteId} title={p.title} docKey={docKey} value={p.body_html} onChange={(v) => set({ body_html: v })} />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }} label={(
                                <Space>{t('Excerpt')}
                                    <Button size="small" type="link" style={{ padding: 0 }} icon={<ThunderboltOutlined />} loading={aiBusy === 'excerpt'}
                                        onClick={() => assist('excerpt', (r) => r?.excerpt && set({ excerpt: r.excerpt }))}>{t('Generate excerpt')}</Button>
                                </Space>
                            )}>
                                <Input.TextArea value={p.excerpt} maxLength={1000} autoSize={{ minRows: 2 }} placeholder={t('Leave empty to use the start of the post')} onChange={(e) => set({ excerpt: e.target.value })} />
                            </Form.Item>
                        </Form>
                    </Card>
                    <Collapse style={{ marginBottom: 16 }} items={[{
                        key: 'seo', label: t('Search engine listing'),
                        children: <SeoPanel siteId={siteId} p={p} set={set} url={url} siteHost={base.replace(/^https?:\/\//, '')} live={live} slugChanged={!!p.public_slug && p.slug !== p.public_slug}
                            suggesting={aiBusy === 'seo'} onSuggest={() => assist('seo', (r) => r?.seo && set((x) => ({ seo: { ...x.seo, ...r.seo } })))} />,
                    }]} />
                </Col>
                <Col xs={24} lg={8}>
                    <Card title={t('Publish date')} style={{ marginBottom: 16 }}>
                        <DatePicker showTime style={{ width: '100%' }} placeholder={t('Publish now')} value={p.published_at ? dayjs(String(p.published_at).replace(' ', 'T')) : null}
                            onChange={(d) => set({ published_at: d ? d.format('YYYY-MM-DD HH:mm:ss') : null })} />
                    </Card>
                    <Card title={t('Cover image')} style={{ marginBottom: 16 }}>
                        <CoverPicker siteId={siteId} src={p.cover?.src} onChange={(m) => set({ cover: m ? { src: m.url, alt: m.alt || p.cover?.alt || '' } : null })} />
                        {p.cover?.src && <Input style={{ marginTop: 8 }} placeholder={t('Alt text')} value={p.cover?.alt || ''} onChange={(e) => set({ cover: { ...p.cover, alt: e.target.value } })} />}
                    </Card>
                    <Card title={t('Organization')} style={{ marginBottom: 16 }}>
                        <Form layout="vertical">
                            <Form.Item label={t('Author')}>
                                {lookup.authors.length > 0 ? (
                                    <Select allowClear style={{ width: '100%' }} value={p.author_id || undefined} placeholder={p.author || lookup.defaultAuthor}
                                        options={lookup.authors.map((a) => ({ value: a.id, label: a.name }))} onChange={(v) => set({ author_id: v || null })} />
                                ) : <Input value={p.author} placeholder={lookup.defaultAuthor} onChange={(e) => set({ author: e.target.value })} />}
                            </Form.Item>
                            <Form.Item label={t('Categories')}>
                                <Select mode="multiple" style={{ width: '100%' }} value={p.category_ids} optionFilterProp="label"
                                    options={lookup.categories.map((c) => ({ value: c.id, label: c.name }))} onChange={(v) => set({ category_ids: v })} />
                            </Form.Item>
                            <Form.Item label={t('Series')}>
                                <Space.Compact style={{ width: '100%' }}>
                                    <Select allowClear style={{ flex: 1, minWidth: 0 }} value={p.series_id || undefined} options={lookup.series.map((c) => ({ value: c.id, label: c.name }))} onChange={(v) => set({ series_id: v || null })} />
                                    <InputNumber min={1} style={{ width: 80 }} placeholder="#" aria-label={t('Position')} disabled={!p.series_id} value={p.series_position || null} onChange={(v) => set({ series_position: v })} />
                                </Space.Compact>
                            </Form.Item>
                            <Form.Item>
                                <Space>
                                    <Checkbox checked={!!p.featured} onChange={(e) => set({ featured: e.target.checked })}>{t('Featured')}</Checkbox>
                                    <Checkbox checked={!!p.pinned} onChange={(e) => set({ pinned: e.target.checked })}>{t('Pinned')}</Checkbox>
                                </Space>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }} label={(
                                <Space>{t('Tags')}
                                    <Button size="small" type="link" style={{ padding: 0 }} icon={<ThunderboltOutlined />} loading={aiBusy === 'tags'}
                                        onClick={() => assist('tags', (r) => r?.tags?.length && set((x) => ({ tags: [...new Set([...(x.tags || []), ...r.tags])] })))}>{t('Suggest tags')}</Button>
                                </Space>
                            )}>
                                <TagsInput format="array" spaceSeparates={false} value={p.tags} onChange={(v) => set({ tags: v })} />
                            </Form.Item>
                        </Form>
                    </Card>
                    {hasId && (
                        <Popconfirm title={t('Delete this post?')} okButtonProps={{ danger: true }} onConfirm={remove}>
                            <Button danger block icon={<DeleteOutlined />}>{t('Delete post')}</Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
            <div style={{ position: 'sticky', bottom: 0, zIndex: 5, display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 0', background: 'var(--ant-color-bg-layout, #f5f5f5)' }}>
                <Text type="secondary" style={{ marginRight: 'auto', alignSelf: 'center' }} data-testid="save-state">
                    {dirty ? t('Unsaved changes') : savedAt ? t('Draft saved {{time}}', { time: fmtDate(savedAt) }) : ''}
                </Text>
                <Space>
                    <Button loading={saving} onClick={() => commit('save')}>{t('Save draft')}</Button>
                    {published ? (
                        <Space.Compact>
                            <Button type="primary" loading={saving} onClick={() => commit('update')}>{t('Update')}</Button>
                            <Dropdown trigger={['click']} menu={{ items: [{ key: 'unpublish', label: t('Unpublish') }], onClick: () => commit('unpublish') }}>
                                <Button type="primary" icon={<DownOutlined />} aria-label={t('More')} />
                            </Dropdown>
                        </Space.Compact>
                    ) : (
                        <Button type="primary" loading={saving} onClick={() => commit('publish')}>{future ? t('Schedule') : t('Publish')}</Button>
                    )}
                </Space>
            </div>
            {hasId && <RevisionsDrawer open={history} onClose={() => setHistory(false)} api={api} postId={idRef.current} onRestored={(d) => { adopt(d); setDocKey((k) => k + 1); message.success(t('Revision restored. Review it, then save.')); }} />}
        </div>
    );
};

export default PostEdit;
